import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calculator, Plus, Minus, X as Multiply, Divide } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomReportBuilder() {
  const [reportName, setReportName] = useState('');
  const [formula, setFormula] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [aggregations, setAggregations] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [savedReports, setSavedReports] = useState([]);

  const availableFields = [
    'product_name',
    'client_name',
    'agent_name',
    'quantity',
    'revenue',
    'profit',
    'cost',
    'period'
  ];

  useEffect(() => {
    fetchSavedReports();
  }, []);

  const fetchSavedReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/custom/list`);
      setSavedReports(response.data);
    } catch (error) {
      console.error('Error fetching saved reports:', error);
    }
  };

  const addField = (field) => {
    if (!selectedFields.includes(field)) {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const removeField = (field) => {
    setSelectedFields(selectedFields.filter(f => f !== field));
    const newAgg = { ...aggregations };
    delete newAgg[field];
    setAggregations(newAgg);
  };

  const setFieldAggregation = (field, agg) => {
    setAggregations({ ...aggregations, [field]: agg });
  };

  const generateReport = async () => {
    if (!reportName || selectedFields.length === 0) {
      toast.error('Please enter report name and select at least one field');
      return;
    }

    try {
      const response = await axios.post(`${API}/reports/custom`, {
        name: reportName,
        formula: formula || 'custom',
        fields: selectedFields,
        aggregations: aggregations
      });

      setPreviewData(response.data.data);
      toast.success('Custom report generated successfully!');
      fetchSavedReports();
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Custom Report Builder
        </h1>
        <p className="text-gray-400">Create custom reports with formulas and aggregations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-[#18181b] rounded-xl border border-white/10 p-6">
            <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Report Configuration
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Report Name
                </label>
                <Input
                  placeholder="e.g., Monthly Revenue Analysis"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  className="bg-[#27272a] border-white/20"
                  data-testid="report-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Formula (Optional)
                </label>
                <Input
                  placeholder="e.g., revenue - cost"
                  value={formula}
                  onChange={(e) => setFormula(e.target.value)}
                  className="bg-[#27272a] border-white/20 font-mono text-sm"
                  data-testid="formula-input"
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-xs"
                    onClick={() => setFormula(formula + '+')}
                  >
                    <Plus size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-xs"
                    onClick={() => setFormula(formula + '-')}
                  >
                    <Minus size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-xs"
                    onClick={() => setFormula(formula + '*')}
                  >
                    <Multiply size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-xs"
                    onClick={() => setFormula(formula + '/')}
                  >
                    <Divide size={14} />
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Fields
                </label>
                <Select onValueChange={addField}>
                  <SelectTrigger className="bg-[#27272a] border-white/20" data-testid="field-select">
                    <SelectValue placeholder="Add field..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/20">
                    {availableFields.map(field => (
                      <SelectItem key={field} value={field}>
                        {field}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Selected Fields
                </label>
                <div className="space-y-2">
                  {selectedFields.map(field => (
                    <div key={field} className="flex items-center gap-2 p-2 bg-[#27272a] rounded-md border border-white/10">
                      <span className="flex-1 text-sm text-white font-mono">{field}</span>
                      <Select
                        value={aggregations[field] || 'none'}
                        onValueChange={(value) => setFieldAggregation(field, value)}
                      >
                        <SelectTrigger className="w-24 h-8 text-xs bg-[#18181b] border-white/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#27272a] border-white/20">
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="sum">Sum</SelectItem>
                          <SelectItem value="product">Product</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeField(field)}
                        className="h-8 px-2"
                      >
                        <Multiply size={14} />
                      </Button>
                    </div>
                  ))}
                  {selectedFields.length === 0 && (
                    <p className="text-sm text-gray-500">No fields selected</p>
                  )}
                </div>
              </div>

              <Button
                onClick={generateReport}
                className="w-full bg-cyan-500 hover:bg-cyan-600"
                data-testid="generate-report-button"
              >
                <Calculator size={16} className="mr-2" />
                Generate Report
              </Button>
            </div>
          </div>

          <div className="bg-[#18181b] rounded-xl border border-white/10 p-6">
            <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Saved Reports ({savedReports.length})
            </h3>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {savedReports.map((report, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-[#27272a] rounded-md border border-white/10"
                    data-testid={`saved-report-${idx}`}
                  >
                    <p className="text-sm font-medium text-white">{report.name}</p>
                    <p className="text-xs text-gray-400 font-mono mt-1">{report.formula}</p>
                  </div>
                ))}
                {savedReports.length === 0 && (
                  <p className="text-sm text-gray-500">No saved reports yet</p>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-[#18181b] rounded-xl border border-white/10 p-6">
            <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Report Preview
            </h3>
            
            {previewData.length > 0 ? (
              <ScrollArea className="h-[600px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      {Object.keys(previewData[0]).map((key) => (
                        <TableHead key={key} className="text-gray-300">
                          {key}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.map((row, idx) => (
                      <TableRow key={idx} className="border-white/10" data-testid={`preview-row-${idx}`}>
                        {Object.values(row).map((value, cellIdx) => (
                          <TableCell key={cellIdx} className="text-gray-200 font-mono text-sm">
                            {typeof value === 'number' ? value.toFixed(2) : value}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="flex flex-col items-center justify-center h-[600px] text-gray-400">
                <Calculator size={64} className="mb-4 opacity-20" />
                <p className="text-lg">No report generated yet</p>
                <p className="text-sm mt-2">Configure your report and click Generate Report</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}